/** Partner console — Add Commercial form (dedicated route).
 *
 * `/partner/commercials/new` — register a commercial using the shared
 * CommercialForm (same form the Edit page uses). On success navigates
 * back to the commercials list. */
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import {
  CommercialForm,
  type CommercialFormValues,
} from "@/components/CommercialForm";
import { PartnerPageHeader } from "@/components/PartnerLayout";
import { Spinner } from "@/components/Spinner";
import { useCreateCommercial, usePartnerMe } from "@/lib/partner";

export default function PartnerAddCommercialPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = usePartnerMe();
  const create = useCreateCommercial();

  if (me.isLoading) return <Spinner fullscreen label={t("common.loading")} />;
  if (me.data && me.data.kind !== "reseller")
    return <Navigate to="/partner" replace />;

  const submit = (v: CommercialFormValues) => {
    const isFixed = v.commission_model === "fixed";
    const termKeys = isFixed
      ? (["fixed_base_eur", "fixed_session_eur", "fixed_signature_eur"] as const)
      : (["rate_base_pct", "rate_session_pct", "rate_signature_pct"] as const);
    const terms: Record<string, string> = {};
    for (const k of termKeys) if (v[k] !== "") terms[k] = v[k];
    create.mutate(
      {
        name: v.name,
        email: v.email,
        phone: v.phone || undefined,
        is_company: v.is_company,
        commission_model: v.commission_model,
        commission_duration: v.commission_duration,
        ...terms,
        ...(v.is_company
          ? {
              legal_name: v.legal_name,
              registration_number: v.registration_number,
              vat_id: v.vat_id,
              address_line: v.address_line,
              city: v.city,
              postal_code: v.postal_code,
              address_country: v.address_country,
            }
          : {}),
      },
      { onSuccess: () => navigate("/partner/commercials") },
    );
  };

  return (
    <>
      <PartnerPageHeader
        eyebrow={t("nav.partner.commercials")}
        title={t("partner.commercials.addTitle", "Add a commercial")}
        description={t(
          "partner.commercials.addLead",
          "Register a new sales commercial. They will get their own referral code and commission terms.",
        )}
      />
      <CommercialForm
        mode="create"
        submitting={create.isPending}
        error={create.isError}
        submitLabel={
          create.isPending
            ? t("partner.commercials.adding")
            : t("partner.commercials.add")
        }
        onSubmit={submit}
        onCancel={() => navigate("/partner/commercials")}
      />
    </>
  );
}
